import { Chip, Tooltip, alpha } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ClaimItemRowForecast } from "@/types";

/**
 * Per-row state chip for a claim line (claim_item) on a claims document.
 *
 * The Claimants tab already shows the per-claimant SL-claim forecast; this chip
 * fills the per-row gap on the Claims tab (and the nested claim-item table under
 * an expanded claimant) by labeling each individual line's disposition.
 *
 * Terminology + coloring deliberately match the claimant-level ForecastChip so
 * the two "What happens" surfaces read as one vocabulary:
 *   - "New"     — success tint — no matching line on file; approving creates a
 *                 new claim_item (mirrors the claimant "New" chip).
 *   - "On file" — outlined / disabled tone — a line matching this row's natural
 *                 key is already on file; approving refreshes it in place
 *                 (mirrors the claimant "No updates" styling).
 *
 * The forecast is backed by the SAME natural-key dedup the loader runs on
 * approve (AB#4456), so the label is an honest preview. "Excluded" is
 * intentionally not modeled — policy exclusions act in the breach/deductible
 * layer, not at claim_item load, so there is no load step for it to mirror.
 */
interface ClaimItemRowStateChipProps {
  forecast: ClaimItemRowForecast | undefined;
}

// Matches the claimant ForecastChip base so the chips are visually identical
// across the two "What happens" surfaces.
const baseChipSx = {
  height: 22,
  "& .MuiChip-label": { px: 0.9, fontSize: "0.72rem", fontWeight: 600 },
};

export const ClaimItemRowStateChip = ({ forecast }: ClaimItemRowStateChipProps) => {
  const { theme } = useAppTheme();

  if (!forecast) return null;

  if (forecast.operation === "update" || forecast.is_duplicate_on_file) {
    return (
      <Tooltip
        title="A matching claim line is already on file. Approving refreshes it in place rather than adding a duplicate."
        placement="top"
        arrow
      >
        <Chip
          label="On file"
          size="small"
          variant="outlined"
          sx={{
            ...baseChipSx,
            borderColor: "divider",
            color: "text.disabled",
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title="New claim line — no match on file. Approving will create this claim item." placement="top" arrow>
      <Chip
        label="New"
        size="small"
        sx={{
          ...baseChipSx,
          bgcolor: alpha(theme.palette.success.main, 0.12),
          color: "success.dark",
        }}
      />
    </Tooltip>
  );
};
